import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api, type Id } from "../lib/api";
import { useCompany } from "../lib/company";
import { routeIdParam, searchIdParam, searchParamsWithId } from "../domain/routeParams";
import { businessScopeDisplayName } from "../domain/businessScopes";
import { policyStatusLabel, policyStatusToneClass } from "../domain/policies";
import {
  buildPolicySectionTree,
  policyClauseIds,
  policySectionDescendantIds,
  type PolicySectionTreeNode,
} from "../domain/policySectionTree";
import {
  mappingTreeSummaries,
  type MappingTreeStatus,
  type MappingTreeSummary,
} from "../domain/mappingTree";
import {
  policyMappingComplianceLabel,
  policyMappingComplianceToneClass,
  policyMappingConfidenceLabel,
  policyMappingConfidenceToneClass,
  policyMappingCoverageLabel,
  policyMappingCoverageToneClass,
  policyMappingVerificationLabel,
  policyMappingVerificationToneClass,
  type PolicyMappingComplianceStatus,
} from "../domain/policyMapping";
import { formatCountLabel } from "../domain/text";
import { caughtErrorMessage } from "../domain/errors";
import { Button, Icon, JurisBadge, cx } from "../components/ui";
import { MappingStatusMark } from "../components/MappingStatusMark";
import { ResponsiveWorkspacePanel } from "../components/ResponsiveWorkspacePanel";
import { MappingWorkspaceLoading, MappingWorkspaceMessage } from "../components/MappingWorkspaceState";
import { moveFocusWithin } from "../lib/accessibility";
import { PolicyAlertRecipientsModal } from "../components/PolicyAlertRecipients";
import { documentOrderNavigation, exactMappingDecisionAvailable, mappingPanelState, mappingSectionSelection } from "../domain/mappingWorkspace";
import { filterPolicyPortfolio, portfolioPolicySelection } from "../domain/policyMappingPortfolio";
import { downloadTextFile } from "../lib/download";
import { persistMappingVerification } from "../lib/mappingVerification";
import { ManualMapping } from "../components/ManualMapping";
import { RichText } from "../components/RichText";
import { richTextDocOrEmpty } from "../domain/richText";
import { useAccess } from "../lib/access";

type PolicyPortfolio = NonNullable<FunctionReturnType<typeof api.policies.policyMappingPortfolio>>;
type PolicyDetail = NonNullable<FunctionReturnType<typeof api.policies.policyMappingMatrixByPolicy>>;
type PolicyRow = PolicyPortfolio["policies"][number];
type PolicySectionRow = PolicyPortfolio["sections"][number];
type PortfolioMappingRow = PolicyPortfolio["mappings"][number];
type MappingRow = PolicyDetail["mappings"][number];
type PolicyId = Id<"companyPolicies">;
type PolicySectionId = Id<"companyPolicySections">;
type PolicyNode = PolicySectionTreeNode<PolicySectionId>;
type SummaryMap = ReadonlyMap<PolicySectionId, MappingTreeSummary>;
type PortfolioBranch = {
  readonly policyId: PolicyId;
  readonly title: string;
  readonly policy: PolicyRow;
  readonly sections: readonly PolicyNode[];
  readonly sectionRows: readonly PolicySectionRow[];
  readonly mappings: readonly PortfolioMappingRow[];
  readonly summaries: SummaryMap;
  readonly status: MappingTreeStatus;
  readonly mappingCount: number;
  readonly mappedSectionCount: number;
  readonly totalSectionCount: number;
  readonly needsReviewCount: number;
  readonly gapCount: number;
};

type PortfolioView = "all" | "gaps" | "review" | "verified";

const EMPTY_POLICY_SECTIONS: readonly PolicySectionRow[] = [];
const EMPTY_POLICY_MAPPINGS: readonly MappingRow[] = [];
const EMPTY_POLICY_TREE: readonly PolicyNode[] = [];
const EMPTY_MAPPING_SUMMARIES: SummaryMap = new Map();

const COMPLIANCE_PRIORITY = {
  doesNotComply: 0,
  needsLegalReview: 1,
  notMapped: 2,
  partiallyComplies: 3,
  unclear: 4,
  complies: 5,
} satisfies Record<PolicyMappingComplianceStatus, number>;

export function PolicyMappingDetail() {
  const { canWrite } = useAccess();
  const [editingMapping, setEditingMapping] = useState<MappingRow | "new" | null>(null);
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const routePolicyId = routeIdParam<"companyPolicies">(params, "id");
  const requestedPolicyId = searchIdParam<"companyPolicies">(searchParams, "policy");
  const requestedSectionId = searchIdParam<"companyPolicySections">(searchParams, "section");
  const { companyId } = useCompany();
  const portfolio = useQuery(
    api.policies.policyMappingPortfolio,
    companyId !== null ? { companyId } : "skip",
  );
  const setVerification = useMutation(api.policies.setObligationMappingVerificationStatus);

  const [selectedPolicyId, setSelectedPolicyId] = useState<PolicyId | null>(null);
  const [selectedId, setSelectedId] = useState<PolicySectionId | null>(null);
  const [treeOpen, setTreeOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [portfolioView, setPortfolioView] = useState<PortfolioView>("all");
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<PolicySectionId>>(() => new Set());
  const [expandedPolicyIds, setExpandedPolicyIds] = useState<ReadonlySet<PolicyId>>(() => new Set());
  const [verificationBusy, setVerificationBusy] = useState<Id<"policyObligationMappings"> | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [staffAssignmentOpen, setStaffAssignmentOpen] = useState(false);
  const treeOpenerRef = useRef<HTMLButtonElement>(null);
  const mappingOpenerRef = useRef<HTMLButtonElement>(null);
  const closeTree = useCallback(() => setTreeOpen(false), []);
  const closeMappings = useCallback(() => setMappingOpen(false), []);
  const policyDetail = useQuery(
    api.policies.policyMappingMatrixByPolicy,
    companyId !== null && selectedPolicyId !== null ? { companyId, policyId: selectedPolicyId } : "skip",
  );

  const branches = useMemo<readonly PortfolioBranch[]>(() => {
    const sectionsByPolicy = new Map<PolicyId, PolicySectionRow[]>();
    const mappingsByPolicy = new Map<PolicyId, PortfolioMappingRow[]>();
    for (const section of portfolio?.sections ?? []) {
      const rows = sectionsByPolicy.get(section.policyId) ?? [];
      rows.push(section);
      sectionsByPolicy.set(section.policyId, rows);
    }
    for (const mapping of portfolio?.mappings ?? []) {
      const rows = mappingsByPolicy.get(mapping.policyId) ?? [];
      rows.push(mapping);
      mappingsByPolicy.set(mapping.policyId, rows);
    }
    return [...(portfolio?.policies ?? [])]
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((policy) => {
        const sectionRows = sectionsByPolicy.get(policy._id) ?? [];
        const mappings = mappingsByPolicy.get(policy._id) ?? [];
        const sections = buildPolicySectionTree(sectionRows.map((section) => ({
          id: section._id,
          parentSectionId: section.parentSectionId,
          nodeKind: section.nodeKind,
          sectionKey: section.sectionKey,
          heading: section.heading,
          order: section.order,
        })));
        const parentIds = policyTreeParentIds(sections);
        const summaries = mappingTreeSummaries(
          sectionRows.map((section) => ({ id: section._id, parentId: parentIds.get(section._id) ?? null })),
          mappings.flatMap((mapping) => mapping.policySectionIds.map((sectionId) => ({
            sectionId,
            verificationStatus: mapping.verificationStatus,
          }))),
        );
        const stats = policyMappingStats(sectionRows, mappings);
        return {
          policyId: policy._id,
          title: policy.title,
          policy,
          sections,
          sectionRows,
          mappings,
          summaries,
          status: rootStatus(summaries, sectionRows.length),
          mappingCount: mappings.length,
          mappedSectionCount: stats.mappedSections,
          totalSectionCount: stats.totalSections,
          needsReviewCount: stats.unapproved,
          gapCount: Math.max(0, stats.totalSections - stats.mappedSections),
        };
      });
  }, [portfolio]);
  const filteredBranches = useMemo(() => filterPolicyPortfolio(branches, filter).filter((branch) => {
    if (portfolioView === "gaps") return branch.gapCount > 0 || branch.mappings.some((mapping) => ["doesNotComply", "partiallyComplies", "notMapped"].includes(mapping.complianceStatus));
    if (portfolioView === "review") return branch.needsReviewCount > 0;
    if (portfolioView === "verified") return branch.mappingCount > 0 && branch.mappings.every((mapping) => mapping.verificationStatus === "humanVerified") && branch.gapCount === 0;
    return true;
  }), [branches, filter, portfolioView]);
  const selectedBranch = branches.find((branch) => branch.policyId === selectedPolicyId) ?? null;
  const selectedPolicy = selectedBranch?.policy ?? null;
  const sections = selectedBranch?.sectionRows ?? EMPTY_POLICY_SECTIONS;
  const mappings = policyDetail?.mappings ?? EMPTY_POLICY_MAPPINGS;
  const tree = selectedBranch?.sections ?? EMPTY_POLICY_TREE;
  const summaries = selectedBranch?.summaries ?? EMPTY_MAPPING_SUMMARIES;
  const policyScope = selectedPolicy?.businessScopeId === undefined || selectedPolicy.businessScopeId === null
    ? null
    : portfolio?.scopes.find((scope) => scope._id === selectedPolicy.businessScopeId) ?? null;

  useEffect(() => {
    if (portfolio === undefined) return;
    const policySelectionOrder = [...branches]
      .sort((a, b) => attentionRank(a) - attentionRank(b) || a.title.localeCompare(b.title))
      .map((branch) => branch.policyId);
    const next = portfolioPolicySelection({
      orderedIds: policySelectionOrder,
      routeId: routePolicyId,
      requestedId: requestedPolicyId,
      currentId: selectedPolicyId,
    });
    if (next !== selectedPolicyId) setSelectedPolicyId(next);
  }, [branches, portfolio, requestedPolicyId, routePolicyId, selectedPolicyId]);

  useEffect(() => {
    if (selectedPolicyId === null) return;
    setExpandedPolicyIds((current) => current.has(selectedPolicyId) ? current : new Set([...current, selectedPolicyId]));
  }, [selectedPolicyId]);

  useEffect(() => {
    if (portfolio === undefined) return;
    if (sections.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const policyOverviewRequested = requestedSectionId === undefined
      && (routePolicyId !== undefined || searchParams.has("policy"));
    if (policyOverviewRequested) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const firstMapped = sections.find((section) => summaries.get(section._id)?.mappingCount !== 0)?._id ?? null;
    const next = mappingSectionSelection({
      orderedIds: sections.slice().sort((a, b) => a.order - b.order).map((section) => section._id),
      requestedId: requestedSectionId,
      currentId: selectedId,
      preferredId: firstMapped,
    });
    if (next !== selectedId) setSelectedId(next);
  }, [portfolio, requestedSectionId, routePolicyId, searchParams, sections, selectedId, summaries]);

  const select = useCallback((policyId: PolicyId, sectionId: PolicySectionId) => {
    const next = searchParamsWithId(searchParamsWithId(searchParams, "policy", policyId), "section", sectionId);
    if (routePolicyId === undefined) setSearchParams(next, { replace: true });
    else navigate(`/mappings/document/${policyId}?section=${sectionId}`, { replace: true });
    if (window.matchMedia("(max-width: 760px)").matches) setTreeOpen(false);
  }, [navigate, routePolicyId, searchParams, setSearchParams]);

  const selectPolicy = useCallback((policyId: PolicyId) => {
    const branch = branches.find((candidate) => candidate.policyId === policyId);
    if (branch === undefined) return;
    setExpandedPolicyIds((current) => new Set([...current, policyId]));
    const next = searchParamsWithId(searchParams, "policy", policyId);
    next.delete("section");
    if (routePolicyId === undefined) setSearchParams(next, { replace: true });
    else navigate(`/mappings/document/${policyId}`, { replace: true });
    if (window.matchMedia("(max-width: 760px)").matches) setTreeOpen(false);
  }, [branches, navigate, routePolicyId, searchParams, setSearchParams]);

  useEffect(() => { setEditingMapping(null); }, [selectedId, selectedPolicyId]);

  const toggleExpanded = useCallback((sectionId: PolicySectionId) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const selected = sections.find((section) => section._id === selectedId) ?? null;
  const selectedNode = selectedId === null ? null : findPolicyNode(tree, selectedId);
  const descendantIds = selectedId === null ? new Set<PolicySectionId>() : policySectionDescendantIds(tree, selectedId);
  const selectedMappings = sortedMappings(mappings.filter((mapping) =>
    mapping.policySectionIds.some((sectionId) => descendantIds.has(sectionId)),
  ));
  const selectedSummary = selectedId === null ? undefined : summaries.get(selectedId);
  const selectedCompliance = selectedMappings.length === 0 ? "notMapped" : mostSevereComplianceStatus(selectedMappings);
  const orderedSectionIds = sections.slice().sort((a, b) => a.order - b.order).map((section) => section._id);
  const { previous: previousSection, next: nextSection } = documentOrderNavigation({
    orderedIds: orderedSectionIds,
    currentId: selectedId,
  });
  const portfolioStats = branches.reduce((total, branch) => ({
    totalSections: total.totalSections + branch.totalSectionCount,
    mappedSections: total.mappedSections + branch.mappedSectionCount,
    reviewCount: total.reviewCount + branch.needsReviewCount,
    mappingCount: total.mappingCount + branch.mappingCount,
  }), { totalSections: 0, mappedSections: 0, reviewCount: 0, mappingCount: 0 });
  const panelState = mappingPanelState({ sectionCount: sections.length, hasSelection: selected !== null, mappingCount: selectedMappings.length });
  const firstAttentionSection = sections
    .slice()
    .sort((a, b) => a.order - b.order)
    .find((section) => {
      const status = summaries.get(section._id)?.status ?? "unmapped";
      return status === "unmapped" || status === "unapproved" || status === "mixed";
    }) ?? null;

  const updateVerification = async (mappingId: Id<"policyObligationMappings">, status: "humanVerified" | "rejected") => {
    if (companyId === null) return;
    await persistMappingVerification({
      mappingId,
      status,
      mutate: ({ mappingId: nextMappingId, status: nextStatus }) => setVerification({ companyId, mappingId: nextMappingId, status: nextStatus }),
      errorMessage: (error) => caughtErrorMessage(error, "Could not update this mapping. Try again."),
      onBusyChange: setVerificationBusy,
      onErrorChange: setVerificationError,
    });
  };

  useEffect(() => setVerificationError(null), [selectedId]);

  const exportSelectedSection = () => {
    if (selected === null) return;
    const filename = `${selectedPolicy?.title ?? "document"}-${selected.sectionKey}`.toLocaleLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    downloadTextFile({ filename: `${filename || "document-section"}.txt`, text: `${policySectionLabel(selected)}\n\n${selected.body}`, type: "text/plain;charset=utf-8" });
  };

  if (companyId === null) return <MappingWorkspaceMessage title="Choose a company" message="Select a company from the workspace selector in the header to open this document mapping." />;
  if (portfolio === undefined) return <MappingWorkspaceLoading label="Loading document mapping workspace" />;
  if (routePolicyId !== undefined && !branches.some((branch) => branch.policyId === routePolicyId)) return <MappingWorkspaceMessage title="Document not found" message="This document is unavailable in the selected company workspace." actionLabel="Back to Documents" onAction={() => navigate("/documents")} />;
  if (branches.length === 0) return <MappingWorkspaceMessage title="No documents yet" message="Add a company document to begin mapping its sections to legislation." actionLabel="Add document" onAction={() => navigate("/documents/import")} />;
  if (selectedBranch === null || selectedPolicy === null) return <MappingWorkspaceLoading label="Preparing document mapping workspace" />;

  return (
    <div className="ls-workspace-grid relative flex h-full min-h-0 overflow-hidden">
      <ResponsiveWorkspacePanel id="policy-mapping-sections" label="document mapping hierarchy" side="left" open={treeOpen} onClose={closeTree} staticQuery="(min-width: 761px)" openerRef={treeOpenerRef} className="!border-[var(--shell-line)] !bg-[var(--shell)] text-[var(--shell-text)]">
        <div className="border-b border-[var(--shell-line)] bg-[var(--shell-raised)] px-3 pb-3 pt-3">
          <div className="mb-2.5 flex items-start justify-between gap-2">
            <div>
              <div className="text-[12px] font-semibold text-sky-700">Document hierarchy</div>
              <div className="mt-1 text-[11.5px] text-slate-600">{branches.length} documents · {portfolioStats.mappedSections}/{portfolioStats.totalSections} clauses mapped</div>
            </div>
            <div className="hidden items-center gap-2 text-[11px] font-medium sm:flex">
              <button type="button" onClick={() => { setExpandedPolicyIds(new Set(branches.map((branch) => branch.policyId))); setCollapsedIds(new Set()); }} className="rounded px-1.5 py-1 text-slate-600 hover:bg-white hover:text-slate-900">Expand all</button>
              <button type="button" onClick={() => setExpandedPolicyIds(new Set())} className="rounded px-1.5 py-1 text-slate-600 hover:bg-white hover:text-slate-900">Collapse all</button>
            </div>
          </div>
          <div className="flex h-8 items-center gap-2 rounded border border-[var(--shell-line)] bg-white px-2.5 focus-within:border-sky-600">
            <Icon name="search" size={13} className="text-slate-500" />
            <input aria-label="Filter documents and sections" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filter document / clause" className="w-full bg-transparent text-[12px] text-slate-700 outline-none placeholder:text-slate-500" />
          </div>
          <div className="mt-2 grid grid-cols-4 rounded border border-[var(--shell-line)] bg-white p-0.5" role="group" aria-label="Document status filter">
            {(["all", "gaps", "review", "verified"] as const).map((view) => (
              <button
                key={view}
                type="button"
                aria-pressed={portfolioView === view}
                onClick={() => setPortfolioView(view)}
                className={cx("rounded-sm px-1 py-1.5 text-[10.5px] font-semibold capitalize transition", portfolioView === view ? "bg-sky-100 text-sky-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")}
              >{view}</button>
            ))}
          </div>
          <Button className="mt-2 w-full justify-center !border-[var(--shell-line)] !bg-white !text-slate-700 hover:!bg-slate-50" size="sm" variant="outline" icon="plus" onClick={() => navigate("/documents/import")}>Register document</Button>
        </div>
        <div className="flex items-center border-b border-[var(--shell-line)] bg-[var(--shell)] px-3 py-1.5 text-[10.5px] font-medium text-slate-600">
          <span className="flex-1">Document / clause</span><span className="w-14 text-center">Review</span><span className="w-10 text-right">Mapped</span>
        </div>
        <div className="flex-1 overflow-y-auto px-1.5 py-1.5">
          {filteredBranches.length === 0 ? (
            <p className="p-3 text-[12px] text-slate-600">No document clauses match these filters.</p>
          ) : (
            <nav
              aria-label="Document mapping hierarchy"
              onKeyDown={(event) => {
                if (moveFocusWithin({ container: event.currentTarget, target: event.target, key: event.key, selector: "[data-hierarchy-item]", orientation: "vertical" })) event.preventDefault();
              }}
            >
              {filteredBranches.map((branch) => (
                <PolicyPortfolioTree
                  key={branch.policyId}
                  branch={branch}
                  selectedPolicyId={selectedPolicyId}
                  selectedSectionId={selectedId}
                  expanded={filter.trim() !== "" || expandedPolicyIds.has(branch.policyId)}
                  collapsedIds={collapsedIds}
                  forceExpanded={filter.trim() !== ""}
                  onTogglePolicy={() => setExpandedPolicyIds((current) => {
                    const next = new Set(current);
                    if (next.has(branch.policyId)) next.delete(branch.policyId);
                    else next.add(branch.policyId);
                    return next;
                  })}
                  onSelectPolicy={() => selectPolicy(branch.policyId)}
                  onSelectSection={(sectionId) => select(branch.policyId, sectionId)}
                  onToggleSection={toggleExpanded}
                />
              ))}
            </nav>
          )}
        </div>
        <MappingLegend />
      </ResponsiveWorkspacePanel>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b border-slate-300 bg-slate-50/95">
          <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 px-4 py-2.5 sm:px-5">
            <div className="flex min-w-0 items-center gap-2">
              <button ref={treeOpenerRef} type="button" aria-label="Browse document hierarchy" aria-expanded={treeOpen} aria-controls="policy-mapping-sections" onClick={() => { setMappingOpen(false); setTreeOpen(true); }} className="hidden min-h-11 items-center justify-center gap-1.5 rounded px-3 text-[13px] font-semibold text-slate-600 ring-1 ring-slate-300 hover:bg-slate-100 max-[760px]:flex">
                <Icon name="layers" size={16} /><span>Document index</span>
              </button>
              <button ref={mappingOpenerRef} type="button" aria-label="View legislation mappings" aria-expanded={mappingOpen} aria-controls="policy-mapping-details" onClick={() => { setTreeOpen(false); setMappingOpen(true); }} className="hidden min-h-11 items-center justify-center gap-1.5 rounded px-3 text-[13px] font-semibold text-slate-600 ring-1 ring-slate-300 hover:bg-slate-100 max-[1120px]:flex">
                <Icon name="book" size={16} /><span>Evidence</span>
              </button>
              <div className="min-w-0">
                <div className="ls-tech-label truncate text-slate-500">Document / {selectedPolicy.title}{selected === null ? "" : ` / ${selected.sectionKey}`}</div>
                <div className="mt-0.5 flex min-w-0 items-center gap-2">
                  <h2 className="truncate text-[17px] font-semibold leading-tight text-slate-950 lg:text-[19px]">{selected === null ? "Document overview" : policySectionLabel(selected)}</h2>
                  <span className={cx("shrink-0 rounded-[3px] px-1.5 py-0.5 text-[10px] font-semibold ring-1", policyStatusToneClass(selectedPolicy.status))}>{policyStatusLabel(selectedPolicy.status)}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selected !== null && <MappingStatePill summary={selectedSummary} />}
              {selected !== null && <StatusPill className={policyMappingComplianceToneClass(selectedCompliance)} label={policyMappingComplianceLabel(selectedCompliance)} />}
              <Button size="sm" variant="outline" icon="pencil" onClick={() => navigate(`/documents/${selectedPolicy._id}${selected === null ? "" : `?section=${selected._id}`}`)}>Edit wording</Button>
              <Button size="sm" icon="users" onClick={() => setStaffAssignmentOpen(true)}>Assign staff to monitor</Button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto border-t border-slate-200 bg-white px-4 py-2 sm:px-5" role="region" tabIndex={0} aria-label="Selected document mapping readouts">
            <TechnicalReadout label="Clauses mapped" value={`${selectedBranch.mappedSectionCount}/${selectedBranch.totalSectionCount}`} />
            <TechnicalReadout label="Mappings" value={String(selectedBranch.mappingCount)} />
            <TechnicalReadout label="Review" value={String(selectedBranch.needsReviewCount)} tone={selectedBranch.needsReviewCount > 0 ? "amber" : "green"} />
            <TechnicalReadout label="Scope" value={businessScopeDisplayName(policyScope)} wide />
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <div className="ls-workspace-grid min-h-0 min-w-0 flex-1 overflow-y-auto">
            {selected === null ? (
              <section className="ls-document-surface mx-auto min-h-full max-w-[820px] border-x border-slate-300 px-6 py-9 sm:px-10 lg:px-14">
                <div className="ls-tech-label text-slate-500">Document overview</div>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-slate-950">{selectedPolicy.title}</h3>
                <p className="mt-3 max-w-2xl text-[14px] leading-6 text-slate-600">Choose a clause in the document index to inspect its exact wording and legislative evidence. Coverage and review state remain visible at every level of the hierarchy.</p>
                <div className="mt-7 grid gap-px border border-slate-300 bg-slate-300 sm:grid-cols-3">
                  <OverviewMetric label="Document clauses" value={selectedBranch.totalSectionCount} />
                  <OverviewMetric label="Mapped clauses" value={selectedBranch.mappedSectionCount} />
                  <OverviewMetric label="Mappings to review" value={selectedBranch.needsReviewCount} attention={selectedBranch.needsReviewCount > 0} />
                </div>
                <div className="mt-8 border-t border-slate-300 pt-5">
                  <div className="ls-tech-label text-slate-500">Next step</div>
                  {firstAttentionSection === null ? (
                    <p className="mt-2 text-sm text-emerald-700">All mapped clauses in this document have a recorded review decision.</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3 bg-slate-50 px-3 py-3 ring-1 ring-slate-200">
                      <div>
                        <div className="text-[11px] font-semibold text-amber-700">Coverage or review needed</div>
                        <div className="mt-0.5 text-sm font-semibold text-slate-900">{policySectionLabel(firstAttentionSection)}</div>
                      </div>
                      <Button size="sm" iconRight="arrowRight" onClick={() => select(selectedPolicy._id, firstAttentionSection._id)}>Inspect clause</Button>
                    </div>
                  )}
                </div>
              </section>
            ) : (
              <article className="ls-document-surface mx-auto flex min-h-full max-w-[820px] flex-col border-x border-slate-300">
                <div className="flex-1 px-6 py-8 sm:px-10 lg:px-14">
                  <div className="ls-tech-label text-slate-500">Document wording <span className="ls-tech-code font-normal">· {selected.sectionKey}</span></div>
                  <h3 className="mt-2 font-serif text-[22px] font-semibold leading-tight text-slate-950">{selected.heading}</h3>
                  {selectedNode !== null && selectedNode.children.length > 0 && (
                    <p className="mt-4 border-l-2 border-amber-400 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900">
                      This heading includes {formatCountLabel(descendantIds.size - 1, "nested clause")}. Select a leaf clause before making a review decision.
                    </p>
                  )}
                  <div className="mt-7 whitespace-pre-wrap font-serif text-[16px] leading-8 text-slate-800">{selected.body || "No document wording has been saved for this clause."}</div>
                </div>
                <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-slate-300 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
                  <div className="flex gap-2">
                    <Button className="max-[760px]:min-h-11" size="sm" variant="outline" disabled={previousSection === null} onClick={() => previousSection !== null && select(selectedPolicy._id, previousSection)}>Previous clause</Button>
                    <Button className="max-[760px]:min-h-11" size="sm" disabled={nextSection === null} onClick={() => nextSection !== null && select(selectedPolicy._id, nextSection)}>Next clause</Button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" icon="doc" onClick={exportSelectedSection}>Export</Button>
                    <Button size="sm" variant="outline" icon="pencil" onClick={() => navigate(`/documents/${selectedPolicy._id}?section=${selected._id}`)}>Edit wording</Button>
                  </div>
                </div>
              </article>
            )}
          </div>

          <ResponsiveWorkspacePanel id="policy-mapping-details" label="legislation mapping details" side="right" open={mappingOpen} onClose={closeMappings} staticQuery="(min-width: 1121px)" openerRef={mappingOpenerRef} className="overflow-y-auto !bg-[#f3f6f9]">
            <div className="sticky top-0 z-10 border-b border-slate-300 bg-slate-50/95 px-4 py-3 backdrop-blur">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="mt-0.5 text-[14px] font-semibold text-slate-950">Legislative evidence</h3>
                  <p className="mt-0.5 text-[11px] text-slate-500">{selectedMappings.length} mapping{selectedMappings.length === 1 ? "" : "s"}{selectedNode?.children.length ? " · includes nested clauses" : ""}</p>
                </div>
                <Button size="sm" variant="outline" icon="plus" onClick={() => navigate(`/documents/${selectedPolicy._id}${selectedId === null ? "" : `?section=${selectedId}`}`)}>Generate mappings</Button>
              </div>
            </div>
            <div className="space-y-3 p-3">
              {policyDetail === undefined && (
                <div className="border border-slate-300 bg-white px-4 py-8 text-center" role="status">
                  <div className="ls-tech-label text-slate-500">Legislative evidence</div>
                  <p className="mt-2 text-[13px] text-slate-500">Loading legislative evidence…</p>
                </div>
              )}
              {policyDetail !== undefined && selectedNode !== null && selectedNode.children.length > 0 && selectedMappings.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] leading-relaxed text-amber-900">These mappings include nested clauses. Open the exact mapped leaf clause to verify or reject evidence.</div>
              )}
              {canWrite && selected && selected.nodeKind !== "heading" && selectedNode?.children.length === 0 && <>
                {editingMapping === null ? <Button variant="outline" onClick={() => setEditingMapping("new")}>Create manual mapping</Button> : <ManualMapping key={`${selected._id}-${editingMapping === "new" ? "new" : editingMapping._id}`} policySectionId={selected._id} mapping={editingMapping === "new" ? undefined : editingMapping} onClose={() => setEditingMapping(null)} />}
              </>}
              {policyDetail !== undefined && (panelState === "noHierarchy" ? (
                <EmptyPanel text="This document has no clauses to map yet. Add document clauses before creating mappings." />
              ) : panelState === "noSelection" ? (
                <EmptyPanel text="Select a document clause to inspect its legislative evidence." />
              ) : panelState === "empty" && selected !== null ? (
                <EmptyPanel text="No legislative evidence is mapped to this document clause." action={<Button size="sm" icon="plus" onClick={() => navigate(`/documents/${selectedPolicy._id}?section=${selected._id}`)}>Generate mappings</Button>} />
              ) : selectedMappings.map((mapping, index) => (
                <LegislationMappingCard
                  key={mapping._id}
                  index={index + 1}
                  mapping={mapping}
                  busy={verificationBusy === mapping._id}
                  canDecide={canWrite && selected?.nodeKind !== "heading" && exactMappingDecisionAvailable({ selectedId, mappedSectionIds: mapping.policySectionIds, leafSelected: selectedNode?.children.length === 0, evidenceAvailable: mapping.legislation !== null && mapping.section !== null })}
                  onOpen={(legislationId, sectionId) => navigate(`/mappings/legislation/${legislationId}?section=${sectionId}`)}
                  onEdit={() => { if (editingMapping === null || window.confirm("Discard the open mapping draft?")) setEditingMapping(mapping); }}
                  onVerify={() => void updateVerification(mapping._id, "humanVerified")}
                  onReject={() => void updateVerification(mapping._id, "rejected")}
                />
              ))) }
              {verificationError !== null && <p className="rounded bg-rose-50 px-3 py-2 text-[12px] text-rose-700 ring-1 ring-rose-200" role="alert">{verificationError}</p>}
            </div>
          </ResponsiveWorkspacePanel>
        </div>
      </div>
      <PolicyAlertRecipientsModal companyId={companyId} policyId={selectedPolicy._id} open={staffAssignmentOpen} onClose={() => setStaffAssignmentOpen(false)} />
    </div>
  );
}

function PolicyPortfolioTree({ branch, selectedPolicyId, selectedSectionId, expanded, collapsedIds, forceExpanded, onTogglePolicy, onSelectPolicy, onSelectSection, onToggleSection }: {
  readonly branch: PortfolioBranch;
  readonly selectedPolicyId: PolicyId | null;
  readonly selectedSectionId: PolicySectionId | null;
  readonly expanded: boolean;
  readonly collapsedIds: ReadonlySet<PolicySectionId>;
  readonly forceExpanded: boolean;
  readonly onTogglePolicy: () => void;
  readonly onSelectPolicy: () => void;
  readonly onSelectSection: (id: PolicySectionId) => void;
  readonly onToggleSection: (id: PolicySectionId) => void;
}) {
  const active = selectedPolicyId === branch.policyId;
  return (
    <div className="mb-0.5">
      <div className={cx("group relative flex min-h-[36px] w-full items-center pr-1 transition-colors", active ? "bg-sky-100" : "hover:bg-white")}>
        {active && <span className="absolute inset-y-0 left-0 w-0.5 bg-sky-700" />}
        <button type="button" aria-label={expanded ? `Collapse ${branch.title}` : `Expand ${branch.title}`} aria-expanded={expanded} onClick={onTogglePolicy} className="grid h-8 w-6 shrink-0 place-items-center text-slate-500 hover:bg-white hover:text-slate-700">
          <Icon name="chevron" size={14} className={cx("transition-transform", expanded && "rotate-90")} />
        </button>
        <button type="button" data-hierarchy-item aria-current={active && selectedSectionId === null ? "location" : undefined} onClick={onSelectPolicy} className="flex min-w-0 flex-1 items-center gap-1.5 self-stretch text-left">
          <TreeFolderIcon />
          <span className={cx("min-w-0 flex-1 truncate text-[12px] font-semibold", active ? "text-sky-700" : "text-slate-700")} title={branch.title}>{branch.title}</span>
          <span className="flex w-14 shrink-0 justify-center"><MappingStatusMark status={branch.status} /></span>
          <span className="w-10 shrink-0 text-right font-mono text-[9px] tabular-nums text-slate-600" aria-label={`${branch.mappedSectionCount} of ${branch.totalSectionCount} clauses mapped`}>{branch.mappedSectionCount}/{branch.totalSectionCount}</span>
        </button>
      </div>
      {expanded && (
        branch.sections.length === 0
          ? <p className="ml-7 border-l border-[var(--shell-line)] py-1 pl-3 font-mono text-[10px] text-slate-600">No clauses</p>
          : <div className="ml-[11px] border-l border-[var(--shell-line)] pl-[3px]">
              <PolicyTree
                nodes={branch.sections}
                depth={0}
                currentId={active ? selectedSectionId : null}
                summaries={branch.summaries}
                collapsedIds={collapsedIds}
                forceExpanded={forceExpanded}
                onSelect={onSelectSection}
                onToggleExpanded={onToggleSection}
              />
            </div>
      )}
    </div>
  );
}

function PolicyTree({ nodes, depth, currentId, summaries, collapsedIds, forceExpanded, onSelect, onToggleExpanded }: {
  readonly nodes: readonly PolicyNode[];
  readonly depth: number;
  readonly currentId: PolicySectionId | null;
  readonly summaries: SummaryMap;
  readonly collapsedIds: ReadonlySet<PolicySectionId>;
  readonly forceExpanded: boolean;
  readonly onSelect: (id: PolicySectionId) => void;
  readonly onToggleExpanded: (id: PolicySectionId) => void;
}) {
  return (
    <div className={cx(depth > 0 && "ml-[11px] border-l border-[var(--shell-line)] pl-[3px]")}>
      {nodes.map((node) => {
        const active = node.id === currentId;
        const hasChildren = node.children.length > 0;
        const expanded = forceExpanded || !collapsedIds.has(node.id);
        const summary = summaries.get(node.id);
        return (
          <div key={node.id}>
            <div className={cx("group relative flex h-[31px] w-full items-center pr-1 transition-colors", active ? "bg-sky-100 ring-1 ring-inset ring-sky-200" : "hover:bg-white")}>
              {active && <span className="absolute inset-y-0 left-0 w-0.5 bg-sky-700" />}
              {hasChildren ? (
                <button type="button" aria-label={expanded ? `Collapse ${node.heading}` : `Expand ${node.heading}`} aria-expanded={expanded} onClick={() => onToggleExpanded(node.id)} className="grid h-[28px] w-5 shrink-0 place-items-center text-slate-500 hover:bg-white hover:text-slate-700">
                  <Icon name="chevron" size={13} className={cx("transition-transform", expanded && "rotate-90")} />
                </button>
              ) : <span className="w-5 shrink-0" />}
              <button type="button" data-hierarchy-item aria-current={active ? "location" : undefined} onClick={() => onSelect(node.id)} className="flex min-w-0 flex-1 items-center gap-1.5 self-stretch text-left">
                {hasChildren ? <TreeFolderIcon /> : <TreeLeafIcon />}
                <span className={cx("min-w-0 flex-1 truncate text-[11.5px]", active ? "font-medium text-sky-700" : hasChildren ? "font-semibold text-slate-700" : "text-slate-600 group-hover:text-slate-900")}>{policySectionLabel(node)}</span>
                <span className="flex w-14 shrink-0 justify-center"><MappingStatusMark status={summary?.status ?? "unmapped"} /></span>
                <span className="w-10 shrink-0 text-right font-mono text-[9px] tabular-nums text-slate-600" aria-label={`${summary?.mappingCount ?? 0} mapping relations`}>{summary?.mappingCount ?? 0}</span>
              </button>
            </div>
            {hasChildren && expanded && <PolicyTree nodes={node.children} depth={depth + 1} currentId={currentId} summaries={summaries} collapsedIds={collapsedIds} forceExpanded={forceExpanded} onSelect={onSelect} onToggleExpanded={onToggleExpanded} />}
          </div>
        );
      })}
    </div>
  );
}

function LegislationMappingCard({ index, mapping, busy, canDecide, onOpen, onEdit, onVerify, onReject }: {
  readonly index: number;
  readonly mapping: MappingRow;
  readonly busy: boolean;
  readonly canDecide: boolean;
  readonly onOpen: (legislationId: Id<"legislation">, sectionId: Id<"sections">) => void;
  readonly onEdit: () => void;
  readonly onVerify: () => void;
  readonly onReject: () => void;
}) {
  return (
    <article aria-busy={busy} className="border border-slate-300 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="ls-tech-label text-slate-500">Mapping {String(index).padStart(2, "0")}</div>
        <StatusPill className={policyMappingVerificationToneClass(mapping.verificationStatus)} label={policyMappingVerificationLabel(mapping.verificationStatus)} />
      </div>
      <div className="px-3 py-3">
        <div className="flex items-center gap-2">
          {mapping.legislation !== null && <JurisBadge code={mapping.legislation.jurisdiction} />}
          <button type="button" disabled={mapping.legislation === null || mapping.section === null} onClick={() => mapping.legislation !== null && mapping.section !== null && onOpen(mapping.legislation._id, mapping.section._id)} className="min-w-0 truncate text-left text-[14px] font-semibold text-slate-900 hover:text-[var(--accent-dark)] disabled:pointer-events-none">{mapping.legislation?.title ?? "Legislation unavailable"}</button>
        </div>
        <div className={cx("mt-1 text-[10.5px] text-slate-500", mapping.legislation !== null && "font-mono")}>{mapping.legislation?.reference ?? "Reference unavailable"}</div>
        <div className="mt-3 border-l-2 border-sky-500 bg-slate-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold text-slate-600">{mapping.source === "ai" ? "AI obligation summary — not statutory wording" : "Recorded mapping obligation"}</div>
          <div className="mt-1 text-[12.5px] font-semibold text-slate-900">{mapping.section === null ? mapping.obligationSection : `${mapping.section.number} ${mapping.section.title}`}</div>
          <p className="mt-1 whitespace-pre-wrap font-serif text-[13px] leading-6 text-slate-700">{mapping.obligationText}</p>
        </div>
        {mapping.section !== null && <details className="mt-3 rounded border border-slate-200 p-3"><summary className="cursor-pointer text-sm font-semibold">View source provision</summary><p className="my-2 text-xs text-amber-800">{mapping.legislation?.legacySnapshot ? "Historical snapshot — current legal status unverified." : "Stored source text; check source provenance in the reader."}</p><div className="max-h-72 overflow-auto"><RichText doc={richTextDocOrEmpty(mapping.section.content)} /></div></details>}
        <div className="mt-3 grid grid-cols-3 gap-px border border-slate-200 bg-slate-200">
          <RelationProperty label="Coverage" value={policyMappingCoverageLabel(mapping.coverageStatus)} className={policyMappingCoverageToneClass(mapping.coverageStatus)} />
          <RelationProperty label="Compliance" value={policyMappingComplianceLabel(mapping.complianceStatus)} className={policyMappingComplianceToneClass(mapping.complianceStatus)} />
          <RelationProperty label="Confidence" value={policyMappingConfidenceLabel(mapping.confidence)} className={policyMappingConfidenceToneClass(mapping.confidence)} />
        </div>
        <MappingText label="Mapping rationale" text={mapping.rationale} />
        {mapping.gap !== "" && <MappingText label="Control gap" text={mapping.gap} tone="red" />}
        {mapping.recommendedAction !== "" && <MappingText label="Required action" text={mapping.recommendedAction} />}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2.5">
        {mapping.legislation !== null && mapping.section !== null && (
          <>
            <Button size="sm" variant="ghost" icon="book" onClick={() => onOpen(mapping.legislation!._id, mapping.section!._id)}>Open source</Button>
            {canDecide && <>
            <Button size="sm" variant="outline" icon="pencil" onClick={onEdit}>Edit mapping</Button>
            <Button className="max-[760px]:min-h-11" size="sm" icon="check" disabled={busy} onClick={onVerify}>{busy ? "Saving…" : "Verify"}</Button>
            <Button className="max-[760px]:min-h-11" size="sm" variant="ghost" icon="trash" disabled={busy} onClick={onReject}>Reject</Button>
            </>}
          </>
        )}
      </div>
    </article>
  );
}

function MappingLegend() {
  return <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-[var(--shell-line)] bg-[var(--shell)] px-4 py-3 text-[10.5px] font-medium text-slate-600"><Legend status="approved" label="Verified" /><Legend status="unapproved" label="Review needed" /><Legend status="mixed" label="Mixed status" /><Legend status="unmapped" label="Not mapped" /></div>;
}

function Legend({ status, label }: { readonly status: MappingTreeStatus; readonly label: string }) {
  return <div className="flex items-center gap-1.5"><MappingStatusMark status={status} /><span>{label}</span></div>;
}

function MappingStatePill({ summary }: { readonly summary: MappingTreeSummary | undefined }) {
  const status = summary?.status ?? "unmapped";
  const label = { unmapped: "Not mapped", unapproved: "Review needed", approved: "Verified", mixed: "Mixed status" }[status];
  return <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200"><MappingStatusMark status={status} />{label}{(summary?.mappingCount ?? 0) > 0 && <span className="font-mono text-[10px] text-slate-600">({summary?.mappingCount})</span>}</span>;
}

function TechnicalReadout({ label, value, tone = "default", wide = false }: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "default" | "amber" | "green";
  readonly wide?: boolean;
}) {
  return (
    <div className={cx("ls-readout shrink-0", wide && "min-w-40")}>
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <span className={cx("ls-readout-value truncate", tone === "amber" && "!text-amber-700", tone === "green" && "!text-emerald-700")}>{value}</span>
    </div>
  );
}

function OverviewMetric({ label, value, attention = false }: { readonly label: string; readonly value: number; readonly attention?: boolean }) {
  return (
    <div className="bg-white px-4 py-4">
      <div className="ls-tech-label text-slate-500">{label}</div>
      <div className={cx("mt-1 font-mono text-2xl font-semibold", attention ? "text-amber-700" : "text-slate-950")}>{value}</div>
    </div>
  );
}

function RelationProperty({ label, value, className }: { readonly label: string; readonly value: string; readonly className: string }) {
  return (
    <div className="min-w-0 bg-white px-2 py-2">
      <div className="text-[10px] font-semibold text-slate-600">{label}</div>
      <span className={cx("mt-1 block truncate rounded-[2px] px-1 py-0.5 text-[9px] font-semibold ring-1", className)} title={value}>{value}</span>
    </div>
  );
}

function TreeFolderIcon() {
  return <span className="relative h-3 w-4 shrink-0 rounded-[2px] bg-slate-500 before:absolute before:-top-0.5 before:left-0.5 before:h-1 before:w-2 before:rounded-t-[2px] before:bg-slate-500" aria-hidden="true" />;
}

function TreeLeafIcon() {
  return <span className="relative h-3.5 w-3 shrink-0 rounded-[1px] border border-sky-600/70 bg-sky-100 before:absolute before:right-0 before:top-0 before:h-1 before:w-1 before:border-b before:border-l before:border-sky-600/70" aria-hidden="true" />;
}

function MappingText({ label, text, tone = "slate" }: { readonly label: string; readonly text: string; readonly tone?: "slate" | "red" }) {
  return <div className="mt-3 border-t border-slate-100 pt-3"><div className="text-[11px] font-semibold text-slate-600">{label}</div><p className={cx("mt-1 text-[12px] leading-relaxed", tone === "red" ? "text-red-700" : "text-slate-600")}>{text}</p></div>;
}

function EmptyPanel({ text, action }: { readonly text: string; readonly action?: React.ReactNode }) {
  return <div className="border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-[13px] text-slate-500"><div className="ls-tech-label mb-2 text-slate-500">Legislative evidence</div><p>{text}</p>{action !== undefined && <div className="mt-4">{action}</div>}</div>;
}

function StatusPill({ className, label }: { readonly className: string; readonly label: string }) {
  return <span className={cx("inline-flex rounded-[3px] px-1.5 py-0.5 text-[10px] font-semibold ring-1", className)}>{label}</span>;
}

const policyTreeParentIds = (nodes: readonly PolicyNode[]): Map<PolicySectionId, PolicySectionId | null> => {
  const result = new Map<PolicySectionId, PolicySectionId | null>();
  const visit = (node: PolicyNode) => {
    result.set(node.id, node.parentId);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return result;
};

const policySectionLabel = (section: { readonly sectionKey: string; readonly heading: string }): string => {
  const comparable = (value: string) => value.toLocaleLowerCase().replace(/[^a-z0-9]/g, "");
  return comparable(section.heading).startsWith(comparable(section.sectionKey))
    ? section.heading
    : `${section.sectionKey} — ${section.heading}`;
};

const findPolicyNode = (nodes: readonly PolicyNode[], id: PolicySectionId): PolicyNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const child = findPolicyNode(node.children, id);
    if (child !== null) return child;
  }
  return null;
};

const rootStatus = (summaries: SummaryMap, sectionCount: number): MappingTreeStatus => {
  let approved = false;
  let unapproved = false;
  let unmapped = sectionCount > 0 && summaries.size === 0;
  for (const summary of summaries.values()) {
    if (summary.status === "approved" || summary.status === "mixed") approved = true;
    if (summary.status === "unapproved" || summary.status === "mixed") unapproved = true;
    if (summary.status === "unmapped") unmapped = true;
  }
  if ((approved || unapproved) && unmapped) return "mixed";
  if (approved && unapproved) return "mixed";
  if (approved) return "approved";
  if (unapproved) return "unapproved";
  return "unmapped";
};

const attentionRank = (branch: PortfolioBranch): number => {
  if (branch.needsReviewCount > 0) return 0;
  if (branch.gapCount > 0) return 1;
  if (branch.mappingCount === 0) return 2;
  return 3;
};

const mostSevereComplianceStatus = (mappings: readonly MappingRow[]): PolicyMappingComplianceStatus => {
  let current: PolicyMappingComplianceStatus = "complies";
  for (const mapping of mappings) if (COMPLIANCE_PRIORITY[mapping.complianceStatus] < COMPLIANCE_PRIORITY[current]) current = mapping.complianceStatus;
  return current;
};

const sortedMappings = (mappings: readonly MappingRow[]): MappingRow[] => [...mappings].sort((a, b) => COMPLIANCE_PRIORITY[a.complianceStatus] - COMPLIANCE_PRIORITY[b.complianceStatus] || b.updatedAt - a.updatedAt);

const policyMappingStats = (sections: readonly PolicySectionRow[], mappings: readonly PortfolioMappingRow[]) => {
  const knownIds = policyClauseIds(sections.map(section => ({ ...section, id: section._id })));
  const mappedIds = new Set(mappings.filter((mapping) => mapping.verificationStatus !== "rejected").flatMap((mapping) => mapping.policySectionIds).filter((id) => knownIds.has(id)));
  return {
    totalSections: knownIds.size,
    mappedSections: mappedIds.size,
    approved: mappings.filter((mapping) => mapping.verificationStatus === "humanVerified").length,
    unapproved: mappings.filter((mapping) => mapping.verificationStatus === "aiUnverified").length,
    legislationCount: new Set(mappings.map((mapping) => mapping.legislationId)).size,
  };
};
